import { memo, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useBlockedNumbers } from '../../../../hooks/useBlockedNumbers'
import AddBlockModal from './AddBlockModal'
import BlockedNumbersFilters from './BlockedNumbersFilters'
import BlockedNumbersTable from './BlockedNumbersTable'
import DeleteConfirmModal from './DeleteConfirmModal'
import EditBlockModal from './EditBlockModal'

function BlockedNumbersManager({ inboundNumber: propInboundNumber }) {
  const inboundNumber = propInboundNumber

  const [searchTerm, setSearchTerm] = useState('')
  const [showActiveOnly, setShowActiveOnly] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)

  const {
    blocks,
    isLoading,
    hasUnsavedChanges,
    refetch,
    addBlock,
    editBlock,
    deleteBlock,
  } = useBlockedNumbers(inboundNumber, {
    localMode: false,
    showActiveOnly,
    onToast: (toastData) => {
      // Only show toast notifications for errors
      if (toastData.color === 'danger' || toastData.color === 'error') {
        toast.error(toastData.title)
      }
    },
  })

  const [showAddModal, setShowAddModal] = useState(false)
  const [editingBlock, setEditingBlock] = useState(null)
  const [deletingBlock, setDeletingBlock] = useState(null)

  // Filter blocks based on search term
  const filteredBlocks = useMemo(() => {
    if (!searchTerm)
      return blocks

    return blocks.filter(block =>
      block.blocked_number?.toLowerCase().includes(searchTerm.toLowerCase())
      || block.reason?.toLowerCase().includes(searchTerm.toLowerCase())
      || block.notes?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
  }, [blocks, searchTerm])

  const handleAddBlock = async (data) => {
    try {
      await addBlock(data)
      setShowAddModal(false)
    }
    catch (error) {
      console.error('Failed to add block:', error)
    }
  }

  const handleEditBlock = async (data) => {
    if (!editingBlock?.id)
      return

    try {
      await editBlock({ blockId: editingBlock.id, updates: data })
      setEditingBlock(null)
    }
    catch (error) {
      console.error('Failed to edit block:', error)
    }
  }

  const handleDeleteBlock = async (block) => {
    setDeletingBlock(block)
  }

  const handleDeleteConfirm = async () => {
    if (!deletingBlock?.id)
      return

    try {
      await deleteBlock(deletingBlock.id)
      setDeletingBlock(null)
    }
    catch (error) {
      console.error('Failed to delete block:', error)
    }
  }

  const isMutating = isLoading

  if (!inboundNumber) {
    return (
      <div className="border border-gray-200 bg-gray-50 p-4 text-gray-700">
        <p>No phone number selected for blocked number management.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-y-4">

      {/* Filters */}
      <BlockedNumbersFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showActiveOnly={showActiveOnly}
        onActiveToggle={setShowActiveOnly}
        onRefresh={refetch}
        onAdd={() => setShowAddModal(true)}
        isLoading={isLoading}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      {/* Table */}
      <BlockedNumbersTable
        blocks={filteredBlocks}
        searchTerm=""
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onEdit={setEditingBlock}
        onDelete={handleDeleteBlock}
        isLoading={isLoading}
      />

      {/* Modals */}
      <AddBlockModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddBlock}
        inboundNumber={inboundNumber}
        isLoading={isMutating}
      />

      <EditBlockModal
        isOpen={!!editingBlock}
        onClose={() => setEditingBlock(null)}
        block={editingBlock}
        onEdit={handleEditBlock}
        isLoading={isMutating}
      />

      <DeleteConfirmModal
        isOpen={!!deletingBlock}
        onClose={() => setDeletingBlock(null)}
        block={deletingBlock}
        onConfirm={handleDeleteConfirm}
        isLoading={isMutating}
      />
    </div>
  )
}

export default memo(BlockedNumbersManager)
